/**
 * The PR watch and Merge (decision 0059), pure: the material hash of a snapshot, the facts two snapshots give, how a
 * merge came about, which PR a human review card may merge, the watch lists and their watchers, the merge sentences,
 * and the projections the web, the turn file and `read_item` read. No I/O, no clock (time arrives as `now`).
 */
import {
  MERGE_RUN_WINDOW_MS,
  type MergeRefusal,
  type MergeReport,
  type MergeResult,
  type MergeVia,
  type MergeView,
  PR_WATCH_COLD_EVERY_MS,
  PR_WATCH_EVERY_MS,
  PR_WATCH_QUIET_DROP_MS,
  type PrFact,
  type PrSnapshot,
  type PrView,
  type PrWatchEntry,
  type PrWatchNotice,
  parseGithubPull,
  type RunnerServes,
  type WatchedPr,
} from "@minga/protocol"
import { canonicalJson, sha256Hex } from "./hash"
import { isWorkingMember, mayRun } from "./multiplayer"
import { PR_WATCH_CANCELLED_KEEP_MS, postedRunsOf, pullRequestHeadSha } from "./pr-runs"
import { isRunEnded, itemOf, runOf } from "./state"
import type { Artifact, Ids, Item, ItemId, ProjectState, Proposal, Run, RunnerId, UserId } from "./types"

// ---------------------------------------------------------------------------------------------------------------
// CONTRACT part: the hash, logins, constants

/** Logins keep their case for display; comparisons are case-insensitive. */
export const normalizeLogin = (l: string): string => l.toLowerCase()

/** Two logins name the same GitHub account (null never matches). */
export const sameLogin = (a: string | null | undefined, b: string | null | undefined): boolean =>
  a != null && b != null && normalizeLogin(a) === normalizeLogin(b)

/**
 * The material projection of a snapshot: what counts as a change. A CI tick (`checks.running` 3 → 2), a mergeability
 * recompute, `updatedAt`, who observed it, excerpts, `mergeState`, the refs and the head author do not.
 */
const material = (s: PrSnapshot): unknown => ({
  artifactId: s.artifactId,
  url: s.url,
  repo: s.repo,
  number: s.number,
  state: s.state,
  draft: s.draft,
  headSha: s.headSha,
  mergeable: s.mergeable,
  reviewDecision: s.reviewDecision,
  reviews: s.reviews.map((r) => [r.login, r.state, r.commitSha, r.at]),
  comments: s.comments.total,
  checks: [s.checks.state, [...s.checks.failing].sort(), s.checks.running > 0],
  mergedBy: s.mergedBy,
  mergeSha: s.mergeSha,
  closedAt: s.closedAt,
  autoMerge: s.autoMerge,
  mergeMethod: s.mergeMethod,
})

/** sha256 hex of the material projection. The runner and the hub both use it: equal hashes mean "no change". */
export const prSnapshotHash = (s: PrSnapshot): string => sha256Hex(canonicalJson(material(s)))

/**
 * `mergeable: "unknown"` keeps the previous known value: GitHub recomputes mergeability after every push to the base.
 * A first snapshot keeps `unknown`.
 */
export const keepKnownMergeable = (prev: PrSnapshot | null, next: PrSnapshot): PrSnapshot =>
  next.mergeable === "unknown" && prev !== null && prev.mergeable !== "unknown"
    ? { ...next, mergeable: prev.mergeable }
    : next

/** The snapshot as stored: metadata only (no review or comment excerpts). */
export const stripExcerpts = (s: PrSnapshot): PrSnapshot => ({
  ...s,
  reviews: s.reviews.map(({ excerpt: _e, ...r }) => r),
  comments: { total: s.comments.total, latest: s.comments.latest.map(({ excerpt: _e, ...c }) => c) },
})

export const short = (sha: string | null | undefined): string => (sha ? sha.slice(0, 7) : "?")

/**
 * A GitHub-supplied name (a login, a check name, a branch) as data for an agent: one line, no backticks, cut to 60
 * characters, rendered as a code span. Check and branch names are free text set by whoever writes the repo's CI.
 */
export const ghName = (x: string): string =>
  `\`${x.replace(/`/g, "'").replace(/\s+/g, " ").trim().slice(0, 60)}\``

const plain = (x: string): string => x

// ---------------------------------------------------------------------------------------------------------------
// Context: who could have acted with the person's gh, which SHAs Minga's runs reported

/** A run's window `[startedAt - 60 s, (endedAt ?? now) + 60 s]`, or null before it started. */
const windowOf = (r: Run, now: number): readonly [number, number] | null =>
  r.startedAt === null ? null : [r.startedAt - MERGE_RUN_WINDOW_MS, (r.endedAt ?? now) + MERGE_RUN_WINDOW_MS]

const inWindows = (at: number | null, windows: ReadonlyArray<readonly [number, number]>): boolean =>
  at !== null && windows.some(([a, b]) => at >= a && at <= b)

/** A run that could have merged with the person's gh: one of theirs, on a runner, in a repo worktree (TRI-18). */
const mergeCandidate = (r: Run, owner: UserId): boolean =>
  r.ownerId === owner && r.executor?._tag === "runner" && !!r.branch

/** The run principal of a PR artifact: the posting run's owner, else the artifact's owner. */
export const prOwnerOf = (s: ProjectState, a: Artifact): UserId =>
  (a.runId ? runOf(s, a.runId)?.ownerId : undefined) ?? a.ownerId

export interface PrContext {
  /** Every `head.sha` and `head.remoteSha` of the item's worker runs, plus done-evidence head SHAs for this URL. */
  readonly knownShas: ReadonlySet<string>
  /** At least one worker run of the item reported a `head` (runner ≥ 0.7). */
  readonly headKnown: boolean
  /** The item has a worker run that has not ended. */
  readonly liveWorker: boolean
  /** The item has any run that has not ended. */
  readonly liveRun: boolean
  /**
   * Windows of the runner runs of the PR's owner over the last day that worked in a repo worktree (`branch` set; any
   * kind): runs that could merge with their gh. A run with no repo (a chat turn, a scratch run) is not a candidate
   * (decision 0070, TRI-18).
   */
  readonly ownerWindows: ReadonlyArray<readonly [number, number]>
  /** Windows of the runs of this item (for `fromRun`). */
  readonly itemWindows: ReadonlyArray<readonly [number, number]>
  /** The item was in review, or had a live run, before this snapshot (a first snapshot of an old PR does not wake). */
  readonly waking: boolean
  /** The artifact's Minga merge, if any. */
  readonly minga: MergeView | undefined
  /** A merge report in this command that names the artifact's current Minga merge. */
  readonly report: MergeReport | undefined
}

const DAY_MS = 86_400_000

/** The context `prFacts` and `mergeVia` read, from the state before the snapshot is applied. */
export const prContext = (s: ProjectState, a: Artifact, now: number, report?: MergeReport): PrContext => {
  const t = a.itemId ? itemOf(s, a.itemId) : undefined
  const itemRuns = a.itemId ? s.runs.filter((r) => r.itemId === a.itemId) : []
  const workers = itemRuns.filter((r) => r.kind === "worker")
  const known = new Set<string>()
  let headKnown = false
  const url = parseGithubPull(a.uri)?.url
  for (const r of workers) {
    if (r.head) {
      headKnown = true
      known.add(r.head.sha)
      if (r.head.remoteSha) known.add(r.head.remoteSha)
    }
    const ev = pullRequestHeadSha(r, url)
    if (ev) known.add(ev)
  }
  const owner = prOwnerOf(s, a)
  const ownerWindows = s.runs
    .filter((r) => mergeCandidate(r, owner) && (r.endedAt === null || r.endedAt >= now - DAY_MS))
    .map((r) => windowOf(r, now))
    .filter((w): w is readonly [number, number] => w !== null)
  const itemWindows = itemRuns
    .filter((r) => r.executor?._tag === "runner")
    .map((r) => windowOf(r, now))
    .filter((w): w is readonly [number, number] => w !== null)
  const liveRun = itemRuns.some((r) => !isRunEnded(r))
  const liveWorker = workers.some((r) => !isRunEnded(r))
  return {
    knownShas: known,
    headKnown,
    liveWorker,
    liveRun,
    ownerWindows,
    itemWindows,
    waking: a.pr !== undefined || t?.status === "in_review" || liveRun,
    minga: a.merge,
    report: report && a.merge?.mergeId === report.mergeId ? report : undefined,
  }
}

/**
 * How a merge came about (§5.2): a person's Merge in Minga (`minga`), a Minga run with the person's gh (`run`), or a
 * person on GitHub (`github`, credited to `mergedBy`; a bot Minga cannot tell from a person reads the same).
 */
export const mergeVia = (next: PrSnapshot, ctx: PrContext): MergeVia => {
  const m = ctx.minga
  const inFlight = m !== undefined && (m.state === "merging" || m.state === "queued")
  // 1. this command carries the order's own successful merge
  if (inFlight && ctx.report?.result._tag === "merged" && ctx.report.result.byOrder) return "minga"
  // 2. the queue or auto-merge finished the person's order (or the watch saw the order's merge before its report)
  const reportSaysOther = ctx.report?.result._tag === "merged" && !ctx.report.result.byOrder
  if (inFlight && !reportSaysOther && sameLogin(next.mergedBy, next.viewerLogin)) return "minga"
  // 3. the runner's gh login merged it while a run of the person was live
  if (sameLogin(next.mergedBy, next.viewerLogin) && inWindows(next.mergedAt, ctx.ownerWindows)) return "run"
  return "github"
}

const isFailing = (s: PrSnapshot | null): boolean =>
  s !== null && (s.checks.state === "failure" || s.checks.state === "error")

/** The facts two snapshots give (§5.2), each with its dedupe key. Excerpts come from `next` (the wire snapshot). */
export const prFacts = (prev: PrSnapshot | null, next: PrSnapshot, ctx: PrContext): ReadonlyArray<PrFact> => {
  const out: PrFact[] = []
  if (next.state === "merged" && prev?.state !== "merged")
    out.push({
      _tag: "merged",
      key: "merged",
      login: next.mergedBy,
      sha: next.mergeSha,
      at: next.mergedAt,
      via: mergeVia(next, ctx),
    })
  if (next.state === "closed" && prev?.state !== "closed")
    out.push({ _tag: "closed", key: `closed:${next.closedAt ?? next.updatedAt}`, at: next.closedAt })
  if (prev !== null) {
    const seenReview = new Set(prev.reviews.map((r) => `${normalizeLogin(r.login)}:${r.at}`))
    const newReviews = next.reviews.filter(
      (r) => !r.bot && !seenReview.has(`${normalizeLogin(r.login)}:${r.at}`),
    )
    for (const r of newReviews.filter((x) => x.state === "changes_requested"))
      out.push({
        _tag: "changes_requested",
        key: `cr:${normalizeLogin(r.login)}:${r.at}`,
        login: r.login,
        writer: r.writer,
        url: r.url,
        ...(r.excerpt !== undefined ? { excerpt: r.excerpt } : {}),
      })
    const prevNewest = prev.comments.latest.reduce((m, c) => Math.max(m, c.at), 0)
    const newComments = next.comments.latest.filter((c) => !c.bot && c.at > prevNewest)
    const said = [
      ...newComments.map((c) => ({ login: c.login, at: c.at, url: c.url, excerpt: c.excerpt })),
      ...newReviews
        .filter((r) => r.state === "commented")
        .map((r) => ({ login: r.login, at: r.at, url: r.url, excerpt: r.excerpt })),
    ].sort((a, b) => a.at - b.at)
    const newest = said[said.length - 1]
    if (newest) {
      const logins: string[] = []
      for (const x of said) if (!logins.some((l) => sameLogin(l, x.login))) logins.push(x.login)
      const fromRun = said.every(
        (x) => sameLogin(x.login, next.viewerLogin) && inWindows(x.at, ctx.itemWindows),
      )
      out.push({
        _tag: "commented",
        key: `cm:${newest.url}`,
        count: said.length,
        logins,
        url: newest.url,
        ...(newest.excerpt !== undefined ? { excerpt: newest.excerpt } : {}),
        fromRun,
      })
    }
  }
  if (isFailing(next) && (!isFailing(prev) || prev === null || next.headSha !== prev.headSha))
    out.push({
      _tag: "ci_failed",
      key: `ci:${next.headSha}`,
      sha: next.headSha,
      failing: [...next.checks.failing],
    })
  if (next.mergeable === "conflicting" && prev?.mergeable !== "conflicting")
    out.push({ _tag: "conflicts", key: `cf:${next.headSha}`, base: next.baseRef })
  if (
    prev !== null &&
    next.headSha !== prev.headSha &&
    !ctx.knownShas.has(next.headSha) &&
    !ctx.liveWorker &&
    ctx.headKnown
  )
    out.push({ _tag: "new_commits", key: `nc:${next.headSha}`, sha: next.headSha, login: next.headAuthor })
  if (next.reviewDecision === "approved" && prev?.reviewDecision !== "approved") {
    const by = [...next.reviews].reverse().find((r) => r.state === "approved")
    out.push({ _tag: "approved", key: `ap:${next.headSha}`, login: by?.login ?? null })
  }
  if (next.checks.state === "success" && (prev?.checks.state !== "success" || prev.headSha !== next.headSha))
    out.push({ _tag: "ci_passed", key: `cp:${next.headSha}`, sha: next.headSha })
  return out
}

/**
 * Does this fact wake the item's agent (§5.2)? Every wake needs `waking`; `merged` not for an item that was already
 * done; `commented` not from a run; a `changes_requested` by the runner's gh login during a run of the item is the
 * run's; `approved` and `ci_passed` never.
 */
export const factWakes = (
  f: PrFact,
  next: PrSnapshot,
  ctx: PrContext,
  itemBefore: Pick<Item, "status"> | undefined,
): boolean => {
  if (!ctx.waking) return false
  switch (f._tag) {
    case "merged":
      return itemBefore?.status !== "done"
    case "commented":
      return !f.fromRun
    case "changes_requested":
      return !isRunReview(f, next, ctx)
    case "approved":
    case "ci_passed":
      return false
    default:
      return true
  }
}

/** A review by the runner's gh login inside a run window of this item is the run's own. */
const isRunReview = (
  f: Extract<PrFact, { _tag: "changes_requested" }>,
  next: PrSnapshot,
  ctx: PrContext,
): boolean => {
  if (!sameLogin(f.login, next.viewerLogin)) return false
  const r = next.reviews.find(
    (x) => sameLogin(x.login, f.login) && f.key === `cr:${normalizeLogin(x.login)}:${x.at}`,
  )
  return r !== undefined && inWindows(r.at, ctx.itemWindows)
}

/** The `changes_requested` fact that sends the item back (§5.3): by a writer, on the current head, not the run's own. */
export const sendBackFact = (
  facts: ReadonlyArray<PrFact>,
  next: PrSnapshot,
  ctx: PrContext,
): Extract<PrFact, { _tag: "changes_requested" }> | undefined => {
  const all = facts.filter(
    (f): f is Extract<PrFact, { _tag: "changes_requested" }> => f._tag === "changes_requested",
  )
  for (let i = all.length - 1; i >= 0; i--) {
    const f = all[i]
    if (!f?.writer || isRunReview(f, next, ctx)) continue
    const r = next.reviews.find((x) => `cr:${normalizeLogin(x.login)}:${x.at}` === f.key)
    if (r && (r.commitSha === null || r.commitSha === next.headSha)) return f
  }
  return undefined
}

/** Wake ranks (§5.4): the strongest waking fact names the `changed` wake. */
export const FACT_RANK: Readonly<Record<PrFact["_tag"], number>> = {
  merged: 3,
  closed: 3,
  changes_requested: 2,
  ci_failed: 2,
  conflicts: 2,
  new_commits: 2,
  commented: 1,
  approved: 0,
  ci_passed: 0,
}

/**
 * One line (≤ 500 chars) of Minga's own words for the waking facts. Never an excerpt; logins, check names and the base
 * branch are code spans (`ghName`): the note reaches agents.
 */
export const factsNote = (
  facts: ReadonlyArray<PrFact>,
  next: PrSnapshot,
  item: Pick<Item, "status"> | undefined,
): string => {
  const parts: string[] = []
  for (const f of facts) {
    switch (f._tag) {
      case "merged":
        parts.push(
          f.via === "run"
            ? `PR #${next.number} merged by a Minga run with ${f.login ? ghName(f.login) : "the person"}'s gh (${short(f.sha)})`
            : f.via === "minga"
              ? `PR #${next.number} merged from Minga (${short(f.sha)})`
              : `PR #${next.number} merged on GitHub by ${f.login ? ghName(f.login) : "someone"} (${short(f.sha)})`,
        )
        if (item && item.status !== "done" && item.status !== "in_review" && item.status !== "cancelled")
          parts.push(`the item is still ${item.status.replace(/_/g, " ")}: close it or plan around it`)
        break
      case "closed":
        parts.push(`PR #${next.number} closed on GitHub without merging`)
        break
      case "changes_requested":
        parts.push(`${ghName(f.login)} requested changes on GitHub`)
        break
      case "commented":
        parts.push(
          `${f.count} ${f.count === 1 ? "comment" : "comments"} by ${f.logins.map(ghName).join(", ")}`,
        )
        break
      case "ci_failed":
        parts.push(
          `CI failed at ${short(f.sha)}${f.failing.length ? ` (${f.failing.slice(0, 5).map(ghName).join(", ")})` : ""}`,
        )
        break
      case "conflicts":
        parts.push(`conflicts with ${ghName(f.base)}`)
        break
      case "new_commits":
        parts.push(`new commits by ${f.login ? ghName(f.login) : "someone"} (head ${short(f.sha)})`)
        break
      default:
        break
    }
  }
  const line = parts.join("; ")
  return line.length > 500 ? `${line.slice(0, 499)}…` : line
}

// ---------------------------------------------------------------------------------------------------------------
// Which PR, which card

/** A live artifact: not rejected and not superseded by a non-rejected artifact. */
const isLive = (s: ProjectState, a: Artifact): boolean =>
  a.state !== "rejected" && !s.artifacts.some((x) => x.supersedes === a.id && x.state !== "rejected")

/** The item's live pull_request artifacts, newest first. */
const livePrsOf = (s: ProjectState, itemId: ItemId): ReadonlyArray<Artifact> =>
  s.artifacts
    .filter((a) => a.itemId === itemId && a.kind === "pull_request" && isLive(s, a))
    .sort((a, b) => b.createdAt - a.createdAt || b.lastSeq - a.lastSeq)

/**
 * The item's own PR: in a workspace repo (`ownRepo`, stamped by the hub), posted by a worker run of the item whose
 * branch is the PR's head. Only its state moves the item (a merge, a close, a writer's changes requested) and only it
 * can be merged; a PR link a run posted for another repo or branch is watched and shown, nothing more. `snap` defaults
 * to the stored snapshot (pass the incoming one while it is being applied).
 */
export const isOwnPr = (
  s: ProjectState,
  a: Artifact,
  snap: PrSnapshot | undefined = a.pr?.snapshot,
): boolean => {
  if (!snap?.ownRepo || a.itemId === null) return false
  const run = a.runId ? runOf(s, a.runId) : undefined
  return run?.kind === "worker" && run.itemId === a.itemId && !!run.branch && snap.headRef === run.branch
}

/**
 * The PR a human review card may merge (§5.5): the newest live pull_request artifact of the item that is its own PR
 * (`isOwnPr`) with an open snapshot.
 */
export const mergeablePrOf = (s: ProjectState, t: Pick<Item, "id">): Artifact | undefined =>
  livePrsOf(s, t.id).find((a) => a.pr?.snapshot.state === "open" && isOwnPr(s, a))

/**
 * Decision 0156: the item's own PR when it is merged (by anyone, after it was posted to Minga), or undefined. A merge
 * ends the item's review loop: no validator round starts on a merged head (REP-66).
 */
export const mergedOwnPrOf = (s: ProjectState, t: Pick<Item, "id">): Artifact | undefined =>
  livePrsOf(s, t.id).find((a) => {
    const snap = a.pr?.snapshot
    return snap?.state === "merged" && snap.mergedAt !== null && snap.mergedAt >= a.createdAt && isOwnPr(s, a)
  })

/**
 * The item's newest PR, merged by a Minga run with the person's gh (§5.3), or undefined. State-driven. Only the item's
 * own PR (`isOwnPr`): a run merging some other PR it linked did not land this item's work.
 */
export const runMergeOf = (s: ProjectState, t: Pick<Item, "id">): Artifact | undefined => {
  const [a] = livePrsOf(s, t.id)
  return a?.pr?.snapshot.state === "merged" && a.pr.mergedVia === "run" && isOwnPr(s, a) ? a : undefined
}

/**
 * The confirm line of a merge by a Minga run (decision 0070, TRI-18): Minga only knows that the person's gh merged it
 * while a run of theirs was live, so it names the run(s) and says "probably". With the state, the candidates are the
 * owner's repo runs whose window holds `mergedAt`; without it (or when none is left), the run is not named.
 * "Merged with jannik-gh's gh while W-12 was running, probably by that run, without your review (PR #48, a1b2c3d):
 * confirm, or follow up."
 */
export const runMergeNotice = (a: Artifact, s?: ProjectState): string => {
  const snap = a.pr?.snapshot
  const at = snap?.mergedAt ?? null
  const owner = s ? prOwnerOf(s, a) : undefined
  const refs =
    s && owner !== undefined && at !== null
      ? s.runs
          .filter(
            (r) =>
              mergeCandidate(r, owner) &&
              r.startedAt !== null &&
              at >= r.startedAt - MERGE_RUN_WINDOW_MS &&
              (r.endedAt === null || at <= r.endedAt + MERGE_RUN_WINDOW_MS),
          )
          .sort((x, y) => (x.startedAt ?? 0) - (y.startedAt ?? 0))
          .map((r) => r.ref)
      : []
  const gh = snap?.mergedBy ? `${snap.mergedBy}'s gh` : "your gh"
  const runs =
    refs.length === 0
      ? "while a Minga run was live, probably by that run"
      : refs.length === 1
        ? `while ${refs[0]} was running, probably by that run`
        : `while ${refs.slice(0, -1).join(", ")} and ${refs[refs.length - 1]} were running, probably by one of them`
  return `Merged with ${gh} ${runs}, without your review (PR #${snap?.number ?? "?"}, ${short(
    snap?.mergeSha,
  )}): confirm, or follow up.`
}

/**
 * The head a `sha_moved` refusal reports (decision 0070, TRI-18): the refusal's detail when it is a SHA (the runner's
 * fresh query, the cloud's snapshot head), else the snapshot's head; null when neither is known, or when it is the SHA
 * the person reviewed (a stale snapshot: the new head is not known yet, so nothing is re-bound until the PR watch
 * reports it).
 */
export const movedHeadOf = (
  detail: string | null,
  snapshot: PrSnapshot | null,
  reviewedSha?: string | null,
): string | null => {
  const head = detail !== null && /^[0-9a-f]{40}$/i.test(detail) ? detail : (snapshot?.headSha ?? null)
  return head !== null && reviewedSha != null && head.toLowerCase() === reviewedSha.toLowerCase()
    ? null
    : head
}

/**
 * The SHA a card offering Merge is bound to (§5.5): the newest worker run of the item that posted the PR and has
 * `head.remoteSha` (`run_head`), else that run's done evidence for this URL (`evidence`), else the snapshot's head.
 */
export const reviewedShaOf = (
  s: ProjectState,
  a: Artifact,
): { readonly sha: string; readonly source: "run_head" | "evidence" | "snapshot" } | null => {
  const url = parseGithubPull(a.uri)?.url
  const posted = postedRunsOf(s, a)
  const withHead = posted.find((r) => r.head?.remoteSha)
  if (withHead?.head?.remoteSha) return { sha: withHead.head.remoteSha, source: "run_head" }
  for (const r of posted) {
    const ev = pullRequestHeadSha(r, url)
    if (ev) return { sha: ev, source: "evidence" }
  }
  const head = a.pr?.snapshot.headSha
  return head ? { sha: head, source: "snapshot" } : null
}

// ---------------------------------------------------------------------------------------------------------------
// Merge sentences (§5.6)

export const mergeSentence = (
  result: MergeResult,
  snapshot: PrSnapshot | null,
  ctx: {
    readonly runnerName?: string
    readonly reviewedSha?: string | null
    readonly runnerSide?: boolean
  } = {},
): string | null => {
  const runner = ctx.runnerName ?? "the runner"
  const base = snapshot?.baseRef ?? "main"
  const repo = snapshot?.repo ?? "the repo"
  const detail = (d: string | null): string => (d ?? "").slice(0, 200)
  switch (result._tag) {
    case "merged":
    case "queued":
      return null
    case "expired":
      return result.sent
        ? `${runner} received the merge but did not report back within 20 minutes. Check the PR on GitHub before merging again.`
        : "No runner of yours was online to merge for 15 minutes. Merge again, or merge on GitHub."
    case "refused":
      return refusalSentence(result.reason, result.detail, { runner, base, repo, snapshot, ctx, detail })
  }
}

const refusalSentence = (
  reason: MergeRefusal,
  raw: string | null,
  x: {
    readonly runner: string
    readonly base: string
    readonly repo: string
    readonly snapshot: PrSnapshot | null
    readonly ctx: { readonly reviewedSha?: string | null; readonly runnerSide?: boolean }
    readonly detail: (d: string | null) => string
  },
): string => {
  const d = x.detail(raw)
  switch (reason) {
    case "sha_moved": {
      // decision 0070 (TRI-18): the card is re-bound to the new head, so Merge again merges that head; when the new
      // head is not known yet (gh's words and a stale snapshot), the card keeps its binding and the old sentence
      const moved = movedHeadOf(raw, x.snapshot, x.ctx.reviewedSha)
      if (moved === null) return "The PR changed since you reviewed it. Look at the new commits, then merge."
      const now = short(moved)
      return `The PR changed since you reviewed it (now ${now}, you reviewed ${short(
        x.ctx.reviewedSha,
      )}). Look at the new commits; Merge again merges ${now}.`
    }
    case "checks_failing": {
      const failing = x.snapshot?.checks.failing ?? []
      return failing.length > 0
        ? `GitHub refused the merge: required check '${failing[0]}' is failing.`
        : "GitHub refused the merge: a required check is failing."
    }
    case "checks_pending":
      return "GitHub refused the merge: required checks are still running. Merge again when they pass."
    case "conflicts":
      return `GitHub refused the merge: the branch conflicts with ${x.base}.`
    case "behind":
      return `GitHub refused the merge: the branch is behind ${x.base}. Update it on GitHub, then merge.`
    case "review_required":
      return "GitHub refused the merge: the branch needs an approving review on GitHub."
    case "protected":
      return `GitHub refused the merge: branch protection does not allow it${d ? ` (${d})` : ""}.`
    case "closed":
      return "The PR was closed on GitHub, so there is nothing to merge."
    case "draft":
      return `The PR is still a draft and gh could not mark it ready${d ? ` (${d})` : ""}.`
    case "gh_logged_out":
      return `gh is not logged in on ${x.runner}. Run \`gh auth login\` there, then merge again.`
    case "gh_missing":
      return `gh is not installed on ${x.runner}.`
    case "no_access":
      return `Your gh login can't merge in ${x.repo}${d ? ` (${d})` : ""}.`
    case "no_method":
      return `${x.repo} allows no merge method gh can use.`
    case "too_late":
      return `${x.runner} got the merge too late to run it safely. Merge again.`
    case "other":
      return x.ctx.runnerSide
        ? `${x.runner} did not run the merge${d ? `: ${d}` : ""}.`
        : `GitHub refused the merge${d ? `: ${d}` : ""}.`
  }
}

/** Runner-side refusals (the order never reached GitHub), for the `other` sentence. */
export const RUNNER_SIDE_DETAILS = [
  "the order is for another runner",
  "the PR is not on this runner's watch list",
  "this runner's signer key is not pinned yet",
] as const

// ---------------------------------------------------------------------------------------------------------------
// Watch lists (§3)

/** At most this many entries per project, newest first. */
export const PR_WATCH_MAX_PER_PROJECT = 200

/**
 * Whose runners watch and merge a PR (§3): the run principal (`ownerId`), the runner that ran the posting run
 * (`openerRunnerId`), and the run's workspace repo (`repoId`).
 */
export const prRoutingOf = (
  s: ProjectState,
  a: Artifact,
): Pick<PrWatchEntry, "ownerId" | "openerRunnerId" | "repoId"> => {
  const run = a.runId ? runOf(s, a.runId) : undefined
  return {
    ownerId: prOwnerOf(s, a),
    openerRunnerId: run?.executor?._tag === "runner" ? run.executor.runnerId : null,
    repoId: run?.placement.requires.repos?.[0] ?? null,
  }
}

/** Every open PR artifact as an entry (§3), newest first, uncapped. */
const allWatchEntries = (s: ProjectState, now: number): ReadonlyArray<PrWatchEntry> => {
  const out: PrWatchEntry[] = []
  for (const a of s.artifacts) {
    if (a.kind !== "pull_request" || !isLive(s, a)) continue
    const pull = parseGithubPull(a.uri)
    if (!pull) continue
    if (a.pr !== undefined && a.pr.snapshot.state !== "open") continue
    const t = a.itemId ? itemOf(s, a.itemId) : undefined
    if (t?.status === "cancelled" && t.closedAt !== null && t.closedAt < now - PR_WATCH_CANCELLED_KEEP_MS)
      continue
    const hot =
      t !== undefined && (t.status === "in_review" || s.runs.some((r) => r.itemId === t.id && !isRunEnded(r)))
    out.push({
      artifactId: a.id,
      itemId: a.itemId,
      url: pull.url,
      repo: pull.repo as PrWatchEntry["repo"],
      number: pull.number,
      ...prRoutingOf(s, a),
      hot,
      createdAt: a.createdAt,
      // wave E (decision 0063): the item's lend at watch time (watchers follow `mayRun`)
      ...(t?.lentRunnerId ? { lentRunnerId: t.lentRunnerId } : {}),
    })
  }
  return out.sort((x, y) => y.createdAt - x.createdAt)
}

/**
 * One entry per open PR artifact (§3), for the Project DO's `PrWatchChanged`, capped at 200 newest first. No hash and
 * no change time in the entries, so a snapshot change never rewrites them.
 */
export const watchEntries = (s: ProjectState, now: number): ReadonlyArray<PrWatchEntry> =>
  allWatchEntries(s, now).slice(0, PR_WATCH_MAX_PER_PROJECT)

/** How many open PR artifacts the cap of 200 left out (for the log line). */
export const watchEntriesCut = (s: ProjectState, now: number): number =>
  Math.max(0, allWatchEntries(s, now).length - PR_WATCH_MAX_PER_PROJECT)

/** A runner as the hub sees it for the watch. */
export interface PrWatchRunner {
  readonly id: RunnerId
  readonly ownerId: UserId
  readonly connected: boolean
  /** When its current connection registered (failover order). */
  readonly connectedAt: number | null
  readonly features: ReadonlyArray<string>
  readonly repoIds: ReadonlyArray<string>
  /** Wave E (decision 0063): absent = `owner` (Just me). */
  readonly serves?: RunnerServes | undefined
}

type EntryLike = Pick<PrWatchEntry, "ownerId" | "openerRunnerId" | "repoId"> &
  Partial<Pick<PrWatchEntry, "lentRunnerId">>

/**
 * Connected runners with `pr_watch` that opened the PR or have its repo (§3) and that may run work for the entry's
 * principal (wave E, decision 0063: `mayRun` at watch time: their own, the team's, or the item's lent runner).
 */
export const watchersOf = (
  entry: EntryLike,
  runners: ReadonlyArray<PrWatchRunner>,
): ReadonlyArray<PrWatchRunner> =>
  runners
    .filter(
      (r) =>
        r.connected &&
        r.features.includes("pr_watch") &&
        mayRun(r, { ownerId: entry.ownerId, lentRunnerId: entry.lentRunnerId ?? null }) &&
        (r.id === entry.openerRunnerId || (entry.repoId !== null && r.repoIds.includes(entry.repoId))),
    )
    .sort((a, b) => (a.connectedAt ?? Number.MAX_SAFE_INTEGER) - (b.connectedAt ?? Number.MAX_SAFE_INTEGER))

/** The one runner that polls a PR: the opener when it is a connected candidate, else the oldest connected one. */
export const primaryWatcherOf = (
  entry: EntryLike,
  runners: ReadonlyArray<PrWatchRunner>,
): PrWatchRunner | undefined => {
  const c = watchersOf(entry, runners)
  return c.find((r) => r.id === entry.openerRunnerId) ?? c[0]
}

/** A hub row: an entry plus what the hub owns. */
export interface PrWatchRow extends PrWatchEntry {
  readonly projectId: Ids["ProjectId"]
  readonly lastHash: string | null
  readonly changedAt: number | null
}

/**
 * The list one runner gets: the entries it is primary for; a cold PR unchanged for 30 days is left out (a hot one
 * never). `lastHash` is the hub's. Decision 0069: rows `covered` by the GitHub App (core `appCovers`) are left out
 * too, so the runner stops polling them; the default covers nothing.
 */
export const watchListFor = (
  runnerId: RunnerId,
  rows: ReadonlyArray<PrWatchRow>,
  runners: ReadonlyArray<PrWatchRunner>,
  now: number,
  covered: (row: PrWatchRow) => boolean = () => false,
): ReadonlyArray<WatchedPr> =>
  rows
    .filter((r) => !covered(r))
    .filter((r) => r.hot || (r.changedAt ?? r.createdAt) >= now - PR_WATCH_QUIET_DROP_MS)
    .filter((r) => primaryWatcherOf(r, runners)?.id === runnerId)
    .map((r) => ({
      artifactId: r.artifactId,
      projectId: r.projectId,
      url: r.url,
      repo: r.repo,
      number: r.number,
      hot: r.hot,
      lastHash: (r.lastHash ?? null) as WatchedPr["lastHash"],
    }))

/** The list hash covers (artifactId, url, repo, number, hot), never `lastHash`. */
export const watchListHash = (list: ReadonlyArray<WatchedPr>): string =>
  sha256Hex(
    canonicalJson(
      [...list]
        .map((p) => [p.artifactId, p.url, p.repo, p.number, p.hot])
        .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    ),
  )

/**
 * Where a merge order goes (§5.8): the opener when connected with `pr_watch`, else the oldest connected watcher of
 * the PR's owner, else null (the order waits).
 */
export const mergeTargetOf = (
  order: EntryLike,
  runners: ReadonlyArray<PrWatchRunner>,
): PrWatchRunner | null => primaryWatcherOf(order, runners) ?? null

// ---------------------------------------------------------------------------------------------------------------
// Projections (§5.8)

export interface PrOfItem {
  readonly artifact: Artifact
  readonly pr: PrView | undefined
  readonly merge: MergeView | undefined
}

/** The item's newest live PR artifact with its `pr` and `merge` views, or null. */
export const prOfItem = (s: ProjectState, itemId: ItemId): PrOfItem | null => {
  const [a] = livePrsOf(s, itemId)
  const merged = s.artifacts
    .filter((x) => x.itemId === itemId && x.kind === "pull_request" && x.state === "published")
    .sort((x, y) => y.createdAt - x.createdAt)[0]
  const pick = a ?? merged
  return pick ? { artifact: pick, pr: pick.pr, merge: pick.merge } : null
}

export type ChipTone = "muted" | "live" | "ink" | "danger" | "warn"

export interface PrChip {
  readonly label: string
  readonly tone: ChipTone
  readonly stale: boolean
  readonly detail: string
}

const hhmm = (ms: number): string => new Date(ms).toISOString().slice(11, 16)

/**
 * The chip on item rows and the item page (§5.8). The first matching row wins. `notice` (the observing runner's
 * PrWatchNotice) decides `stale`; without it `stale` is false (a quiet PR is never stale by its `observedAt` alone).
 */
export const prChip = (
  view: { readonly pr: PrView | undefined; readonly merge: MergeView | undefined },
  item: Pick<Item, "status"> | undefined,
  now: number,
  notice?: PrWatchNotice & { readonly runnerName?: string },
  fmt: (ms: number) => string = hhmm,
  /** How a GitHub-supplied name is shown (`ghName` for an agent; as is for the web). */
  name: (x: string) => string = plain,
): PrChip | null => {
  const snap = view.pr?.snapshot
  if (!snap) return null
  // decision 0069: a snapshot the GitHub App path re-read is seen by GitHub; no runner's notice makes it stale
  const byApp = snap.observedBy === "github_app"
  const runner = notice?.runnerName ?? "the runner"
  let detail = `changed ${fmt(snap.observedAt)} · seen by ${byApp ? "GitHub" : (notice?.runnerName ?? snap.observedBy)}`
  let stale = false
  if (snap.state === "open" && notice !== undefined && !byApp) {
    const cadence = 2 * (view.pr?.snapshot && isHotHint(item) ? PR_WATCH_EVERY_MS : PR_WATCH_COLD_EVERY_MS)
    const old = notice.lastCheckAt === null || notice.lastCheckAt < now - cadence
    if (notice.state !== "watching" || old) {
      stale = true
      const why =
        notice.state === "gh_logged_out"
          ? "gh is logged out"
          : notice.state === "gh_missing"
            ? "gh is not installed"
            : notice.state === "rate_limited"
              ? "GitHub's rate limit is reached"
              : `${runner} is offline`
      detail = `not checked since ${notice.lastCheckAt !== null ? fmt(notice.lastCheckAt) : "the start"} · ${why}`
    }
  }
  const chip = (label: string, tone: ChipTone): PrChip => ({ label, tone, stale, detail })
  const m = view.merge
  if (snap.state === "open" && (m?.state === "merging" || m?.state === "queued"))
    return chip(
      m.state === "merging"
        ? "Merging…"
        : snap.autoMerge === "armed"
          ? "Auto-merge on"
          : "Queued to merge on GitHub",
      "live",
    )
  if (snap.state === "merged") {
    if (view.pr?.mergedVia === "run")
      // decision 0070 (TRI-18): hedged like the confirm notice: the person's gh merged it while a Minga run was live
      return chip(`Merged during a Minga run · ${short(snap.mergeSha)} · not reviewed`, "warn")
    return chip(
      `Merged · ${short(snap.mergeSha)}${snap.mergedBy ? ` · by ${name(snap.mergedBy)}` : ""}${
        snap.mergedAt !== null ? ` · ${fmt(snap.mergedAt)}` : ""
      }`,
      "ink",
    )
  }
  if (snap.state === "closed") return chip("Closed without merging", "muted")
  if (item?.status === "done") return chip("Open · approved in Minga, not merged", "warn")
  if (snap.draft) return chip("Open · draft", "muted")
  if (snap.checks.state === "failure" || snap.checks.state === "error") {
    const n = snap.checks.failing.length
    return chip(
      n > 0
        ? `Open · ${n} ${n === 1 ? "check" : "checks"} failing (${snap.checks.failing.slice(0, 3).map(name).join(", ")})`
        : "Open · checks failing",
      "danger",
    )
  }
  if (snap.mergeable === "conflicting") return chip(`Open · conflicts with ${name(snap.baseRef)}`, "warn")
  if (snap.reviewDecision === "changes_requested") {
    const by = [...snap.reviews].reverse().find((r) => r.state === "changes_requested")
    return chip(`Open · changes requested${by ? ` by ${name(by.login)}` : ""}`, "warn")
  }
  if (snap.checks.running > 0) return chip("Open · checks running", "live")
  if (snap.checks.state === "success") return chip("Open · checks passing", "ink")
  return chip("Open", "muted")
}

const isHotHint = (item: Pick<Item, "status"> | undefined): boolean => item?.status === "in_review"

/**
 * The "On GitHub" strip (§5.8): what happened on GitHub since this review round started. [] when there is nothing
 * to say.
 */
export const onGitHub = (
  view: { readonly pr: PrView | undefined },
  item: Pick<Item, "status" | "review"> | undefined,
  roundStartedAt: number | null,
  /** How a GitHub-supplied name is shown (`ghName` for an agent; as is for the web). */
  name: (x: string) => string = plain,
): ReadonlyArray<string> => {
  const snap = view.pr?.snapshot
  if (!snap) return []
  const out: string[] = []
  const since = roundStartedAt ?? 0
  const comments = snap.comments.latest.filter((c) => !c.bot && c.at > since)
  if (comments.length > 0) {
    const logins: string[] = []
    for (const c of comments) if (!logins.some((l) => sameLogin(l, c.login))) logins.push(c.login)
    out.push(
      `${comments.length} new ${comments.length === 1 ? "comment" : "comments"} from ${logins.map(name).join(", ")}`,
    )
  }
  if (snap.state === "open" && (snap.checks.state === "failure" || snap.checks.state === "error"))
    out.push(
      `checks failing${snap.checks.failing.length ? ` (${snap.checks.failing.slice(0, 3).map(name).join(", ")})` : ""}`,
    )
  const reviewed = view.pr?.reviewed?.sha
  if (snap.state === "open" && reviewed && reviewed !== snap.headSha)
    out.push(`new commits${snap.headAuthor ? ` by ${name(snap.headAuthor)}` : ""} since this round`)
  const approvedBy = [...snap.reviews].reverse().find((r) => r.state === "approved" && !r.bot)
  if (snap.reviewDecision === "approved" && approvedBy)
    out.push(`approved on GitHub by ${name(approvedBy.login)}`)
  if (snap.state === "open" && snap.mergeable === "conflicting")
    out.push(`conflicts with ${name(snap.baseRef)}`)
  void item
  return out
}

export interface ReviewMergeState {
  readonly state: "merging" | "queued" | "armed" | null
  readonly since: number | null
  readonly by: UserId | null
  readonly reviewedSha: string | null
  /**
   * Who may choose Merge. Wave E (decision 0063, answer 5): any project member (not a viewer); it still runs with the
   * watcher's gh (the PR owner's machine, the team's, or a lent one).
   */
  readonly mergeAllowedFor: (userId: UserId) => boolean
}

/** What B2's review card shows about Merge for one proposal (§5.8). */
export const reviewMergeState = (s: ProjectState, proposal: Proposal): ReviewMergeState | null => {
  if (proposal.body._tag !== "question" || proposal.body.subject?._tag !== "item") return null
  const t = itemOf(s, proposal.body.subject.id)
  if (!t) return null
  const a =
    mergeablePrOf(s, t) ??
    s.artifacts.find((x) => x.itemId === t.id && x.merge?.proposalId === proposal.id) ??
    undefined
  if (!a) return null
  const m = a.merge?.proposalId === proposal.id ? a.merge : undefined
  const inFlight = m?.state === "merging" || m?.state === "queued"
  const reviewed = a.pr?.reviewed?.proposalId === proposal.id ? a.pr.reviewed.sha : null
  return {
    state: !inFlight
      ? null
      : m?.state === "merging"
        ? "merging"
        : a.pr?.snapshot.autoMerge === "armed"
          ? "armed"
          : "queued",
    since: inFlight ? (m?.at ?? null) : null,
    by: inFlight ? (m?.by ?? null) : null,
    reviewedSha: reviewed,
    mergeAllowedFor: (userId) => isWorkingMember(s, userId),
  }
}
